import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AlertComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  InputGroupComponent,
  InputGroupTextDirective,
  RowComponent,
  SpinnerComponent,
  TextColorDirective
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { AuthLanguageSwitcherComponent, TranslatePipe } from 'i18n';
import { ENVIRONMENT, Logger, LogoComponent, UnassistedInputDirective } from 'shared';

@Component({
  selector: 'app-reset-password-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    AlertComponent,
    CardComponent,
    CardBodyComponent,
    ColComponent,
    ContainerComponent,
    FormControlDirective,
    InputGroupComponent,
    InputGroupTextDirective,
    RowComponent,
    ButtonDirective,
    IconDirective,
    IconModule,
    SpinnerComponent,
    TextColorDirective,
    UnassistedInputDirective,
    LogoComponent,
    TranslatePipe,
    AuthLanguageSwitcherComponent
  ],
  templateUrl: './reset-password-page.component.html',
  styleUrls: ['./reset-password-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private environment: any = inject(ENVIRONMENT);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('ResetPasswordPageComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  token = signal<string | null>(null);
  password = signal<string>('');
  confirmPassword = signal<string>('');
  isLoading = signal<boolean>(false);
  isVerifying = signal<boolean>(true);
  tokenValid = signal<boolean>(false);
  error = signal<string | null>(null);
  success = signal<boolean>(false);

  passwordsMatch = computed(() => this.password() === this.confirmPassword());

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Register icons
    // IconModule.registerIcons({ cilArrowLeft, cilLockLocked });

    // Extract token from route and verify once
    effect(() => {
      const tokenFromRoute = this.route.snapshot.paramMap.get('token');
      this.token.set(tokenFromRoute);

      if (tokenFromRoute) {
        this.verifyToken(tokenFromRoute);
      } else {
        this.isVerifying.set(false);
        this.error.set('Invalid reset link');
      }
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private verifyToken(token: string): void {
    this.isVerifying.set(true);
    this.error.set(null);

    this.http.get(`${this.environment.apiUrl}/auth/verify_reset_token/${token}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.isVerifying.set(false);
          this.tokenValid.set(res.valid);
          this.logger.debug('Token verification result:', res.valid);
        },
        error: err => {
          this.isVerifying.set(false);
          this.tokenValid.set(false);
          this.logger.error('Token verification failed', err);
          this.error.set(err.error?.error || 'Invalid or expired reset link');
        }
      });
  }

  resetPassword(): void {
    const pwd = this.password().trim();
    const confirm = this.confirmPassword().trim();

    if (!pwd || !confirm) {
      this.error.set('Please enter and confirm your new password');
      return;
    }

    if (pwd !== confirm) {
      this.error.set('Passwords do not match');
      return;
    }

    if (pwd.length < 6) {
      this.error.set('Password must be at least 6 characters');
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.http.post(`${this.environment.apiUrl}/auth/reset_password`, {
      token: this.token(),
      password: pwd
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: () => {
        this.isLoading.set(false);
        this.success.set(true);
        this.logger.debug('Password reset successful');
        setTimeout(() => this.router.navigate(['/login']), 3000);
      },
      error: err => {
        this.isLoading.set(false);
        this.logger.error('Password reset failed', err);
        this.error.set(err.error?.error || 'Failed to reset password. Please try again.');
      }
    });
  }

  backToLogin(): void {
    this.router.navigate(['/login']);
  }
}